import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';

const API_LIST = '/offers';
const API_DETAIL = (id: number) => `/offers/${id}`;

export interface OfferLineItem {
  id?: number;
  offerId?: number;
  sortOrder: number;
  name?: string | null;
  description?: string | null;
  model?: string | null;
  quantity: number;
  unitPrice: number;
  discount?: number | null;
  amount: number;
  catalogItemId?: number | null;
  attributesJson?: Record<string, unknown> | null;
  catalogItem?: {
    id: number;
    name?: string | null;
    primaryFileAttachmentId?: number | null;
    FileAttachment?: Array<{ id: number; filePath: string | null; fileType: string | null }>;
  } | null;
}

export interface Offer {
  id: number;
  clientId?: number | null;
  leadId?: number | null;
  assignedToId?: number | null;
  offerNumber: string;
  description?: string | null;
  offerDate: string;
  validUntil?: string | null;
  validityText?: string | null;
  status: string;
  draftDate?: string | null;
  sendDate?: string | null;
  acceptedDate?: string | null;
  declinedDate?: string | null;
  expiredDate?: string | null;
  companyName?: string | null;
  companyLogoUrl?: string | null;
  companyAddress?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;
  signatoryName?: string | null;
  introText?: string | null;
  deliveryText?: string | null;
  paymentMethodText?: string | null;
  notes?: string | null;
  subtotal?: number | null;
  discount?: number | null;
  vatRate?: number | null;
  total?: number | null;
  currencyCode?: string | null;
  clientInvoiceId?: number | null;
  createdAt?: string;
  updatedAt?: string | null;
  createdById: number;
  updatedById?: number | null;
  client?: { clientId: number; clientName?: string | null; clientName2?: string | null; clientEmail?: string | null; clientAddress?: string | null };
  lead?: { leadId: number; leadDescr?: string | null };
  clientInvoice?: { invoiceId: number; InvoiceNo?: string | null } | null;
  assignedTo?: { id: number; userFirstName: string; userLastName?: string } | null;
  createdBy?: { userFirstName: string; userLastName?: string };
  updatedBy?: { userFirstName: string; userLastName?: string } | null;
  OfferLineItem?: OfferLineItem[];
  FileAttachment?: Array<{
    id: number;
    fileName: string | null;
    filePath: string | null;
    fileType: string | null;
    fileSize: number | null;
    uploadedAt: string;
  }>;
  followUps?: Array<{
    id: number;
    contactedAt: string;
    responseStatus?: string | null;
    responseNote?: string | null;
    responseAt?: string | null;
  }>;
}

export interface OffersListResponse {
  records: Offer[];
}

export const useOffers = (searchParams?: string, filter?: string) => {
  let query = '';
  if (searchParams) query += `?searchParams=${encodeURIComponent(searchParams)}`;
  if (filter) query += query ? `&filter=${encodeURIComponent(filter)}` : `?filter=${encodeURIComponent(filter)}`;

  const { data, error, isLoading, mutate } = useSWR<OffersListResponse>(`${API_LIST}${query}`, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  const records = data?.records ?? [];
  return {
    data: records,
    records,
    isLoading,
    isError: !!error,
    mutate,
  };
};

export const useOffer = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Offer>(id ? API_DETAIL(id) : null, fetcher, {
    revalidateOnFocus: true,
  });
  return {
    data,
    offer: data,
    isLoading,
    isError: !!error,
    mutate,
  };
};
